import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { MapPinIcon, ClockIcon, PhoneIcon, MailIcon, UserIcon, SendIcon, PrinterIcon } from 'lucide-react';
export function ContactPage() {
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useScrollAnimation();
  const {
    ref: infoRef,
    isVisible: isInfoVisible
  } = useScrollAnimation();
  return <main data-id="element-580">
      <PageBanner title="Contact Us" subtitle="We're here to help. Reach out to us anytime to discuss your care needs." breadcrumbs={[{
      label: 'Contact',
      path: '/contact'
    }]} data-id="element-581" />

      <ContactCTA data-id="element-582" />

      {/* Main Contact Section */}
      <section className="py-20 bg-gray-50" data-id="element-583">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-584">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-585">
            {/* Contact Form — takes 3 columns */}
            <div ref={formRef} className={`lg:col-span-3 transition-all duration-1000 ease-out ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-586">
              <div className="bg-white rounded-2xl p-8 md:p-10 border border-gray-300 shadow-lg" data-id="element-587">
                <h3 className="text-2xl font-bold text-dark mb-2" data-id="element-588">
                  Send Us a Message
                </h3>
                <p className="text-dark-muted mb-8" data-id="element-589">
                  Fill out the form below and we'll get back to you as soon as
                  possible.
                </p>

                <form className="space-y-5" onSubmit={e => e.preventDefault()} data-id="element-590">
                  <div data-id="element-591">
                    <label htmlFor="name" className="block text-sm font-semibold text-dark mb-1.5" data-id="element-592">
                      Your Name <span className="text-red-500" data-id="element-593">*</span>
                    </label>
                    <input type="text" id="name" required className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-dark placeholder-gray-400" placeholder="Enter your full name" data-id="element-594" />
                  </div>

                  <div data-id="element-595">
                    <label htmlFor="email" className="block text-sm font-semibold text-dark mb-1.5" data-id="element-596">
                      Your Email <span className="text-red-500" data-id="element-597">*</span>
                    </label>
                    <input type="email" id="email" required className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-dark placeholder-gray-400" placeholder="Enter your email address" data-id="element-598" />
                  </div>

                  <div data-id="element-599">
                    <label htmlFor="subject" className="block text-sm font-semibold text-dark mb-1.5" data-id="element-600">
                      Subject
                    </label>
                    <input type="text" id="subject" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-dark placeholder-gray-400" placeholder="What is this regarding?" data-id="element-601" />
                  </div>

                  <div data-id="element-602">
                    <label htmlFor="message" className="block text-sm font-semibold text-dark mb-1.5" data-id="element-603">
                      Your Message
                    </label>
                    <textarea id="message" rows={6} className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all resize-none text-dark placeholder-gray-400" placeholder="Tell us how we can help you..." data-id="element-604"></textarea>
                  </div>

                  <button type="submit" className="bg-primary hover:bg-primary-dark text-white font-bold py-3.5 px-8 rounded-xl transition-all shadow-lg hover:shadow-2xl flex items-center justify-center group" data-id="element-605">
                    <SendIcon className="mr-2 h-5 w-5 group-hover:translate-x-0.5 transition-transform" data-id="element-606" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar — takes 2 columns */}
            <div ref={infoRef} className={`lg:col-span-2 transition-all duration-1000 delay-200 ease-out ${isInfoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-607">
              <div className="space-y-8" data-id="element-608">
                {/* Director Info */}
                <div className="bg-primary-dark text-white rounded-2xl p-8 shadow-2xl" data-id="element-609">
                  <div className="flex items-center mb-6" data-id="element-610">
                    <div className="bg-white/20 p-3 rounded-xl mr-4" data-id="element-611">
                      <UserIcon className="h-7 w-7 text-secondary" data-id="element-612" />
                    </div>
                    <div data-id="element-613">
                      <h4 className="font-bold text-lg" data-id="element-614">
                        Dr. Ijeoma Nwokorie Igwe
                      </h4>
                      <p className="text-white/70 text-sm font-medium" data-id="element-615">
                        Director
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4 text-white/90" data-id="element-616">
                    <div className="flex items-start" data-id="element-617">
                      <MapPinIcon className="h-5 w-5 text-secondary mr-3 mt-0.5 flex-shrink-0" data-id="element-618" />
                      <div data-id="element-619">
                        <p className="font-semibold text-white" data-id="element-620">Address</p>
                        <p className="text-white/80 text-sm" data-id="element-621">
                          11075 Berry Road, Suite 301-6
                          <br data-id="element-622" />
                          Waldorf, MD 20603
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="bg-white rounded-2xl p-8 border border-gray-300 shadow-lg" data-id="element-623">
                  <h4 className="font-bold text-dark text-lg mb-5 flex items-center" data-id="element-624">
                    <PhoneIcon className="h-5 w-5 text-primary mr-2" data-id="element-625" />
                    Phone Numbers
                  </h4>
                  <div className="space-y-4" data-id="element-626">
                    <a href="tel:2405358731" className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 hover:shadow-md transition-all group" data-id="element-627">
                      <div data-id="element-628">
                        <p className="text-xs font-semibold text-dark-muted uppercase tracking-wider" data-id="element-629">
                          Cell
                        </p>
                        <p className="text-dark font-bold group-hover:text-primary transition-colors" data-id="element-630">
                          (240) 535-8731
                        </p>
                      </div>
                      <PhoneIcon className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" data-id="element-631" />
                    </a>
                    <a href="tel:2407906760" className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 hover:shadow-md transition-all group" data-id="element-632">
                      <div data-id="element-633">
                        <p className="text-xs font-semibold text-dark-muted uppercase tracking-wider" data-id="element-634">
                          Office
                        </p>
                        <p className="text-dark font-bold group-hover:text-primary transition-colors" data-id="element-635">
                          (240) 790-6760
                        </p>
                      </div>
                      <PhoneIcon className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" data-id="element-636" />
                    </a>
                    <div className="flex items-center justify-between p-3 rounded-xl transition-all group" data-id="element-637">
                      <div data-id="element-638">
                        <p className="text-xs font-semibold text-dark-muted uppercase tracking-wider" data-id="element-639">
                          Fax
                        </p>
                        <p className="text-dark font-bold" data-id="element-640">(240) 303-5680</p>
                      </div>
                      <PrinterIcon className="h-4 w-4 text-gray-400" data-id="element-641" />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-white rounded-2xl p-8 border border-gray-300 shadow-lg" data-id="element-642">
                  <h4 className="font-bold text-dark text-lg mb-4 flex items-center" data-id="element-643">
                    <MailIcon className="h-5 w-5 text-primary mr-2" data-id="element-644" />
                    Email
                  </h4>
                  <a href="mailto:edvinhomehealth@gmail.com" className="text-primary font-semibold hover:text-primary-dark hover:underline transition-colors" data-id="element-645">
                    edvinhomehealth@gmail.com
                  </a>
                </div>

                {/* Business Hours */}
                <div className="bg-white rounded-2xl p-8 border border-gray-300 shadow-lg" data-id="element-646">
                  <h4 className="font-bold text-dark text-lg mb-4 flex items-center" data-id="element-647">
                    <ClockIcon className="h-5 w-5 text-secondary mr-2" data-id="element-648" />
                    Business Hours
                  </h4>
                  <div className="space-y-2 text-dark-muted" data-id="element-649">
                    <div className="flex justify-between" data-id="element-650">
                      <span data-id="element-651">Monday – Friday</span>
                      <span className="font-semibold text-dark" data-id="element-652">
                        9:00 AM – 5:00 PM
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-653">
                      <span data-id="element-654">Saturday</span>
                      <span className="font-semibold text-dark" data-id="element-655">
                        By Appointment
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-656">
                      <span data-id="element-657">Sunday</span>
                      <span className="font-semibold text-dark" data-id="element-658">Closed</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-300" data-id="element-659">
                    <p className="text-primary font-bold text-sm" data-id="element-660">
                      🟢 24/7 On-Call Nursing Support Available
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}